// Requêtes de la rubrique Réservations du portail (M19, lot L4-A) : équipements ouverts en ligne d'une
// collectivité avec leurs créneaux de la semaine, mes réservations, demande et annulation.
import type { EtatReservation, PlageCalendrier, Reservation } from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../../api/client';
import { useSessionUsager } from '../session/FournisseurSessionUsager';
import type { TonStatut } from '../statuts';

export const CLES_RESERVATIONS = {
  mes: ['citoyen', 'reservations'] as const,
  equipements: (code: string, debut: string) => ['citoyen', 'equipements', code, debut] as const,
};

export interface EquipementPortail {
  id: string;
  code: string;
  nom: string;
  description: string | null;
  capacite: number | null;
  payant: boolean;
  localisation: string | null;
  jours: { date: string; jour_semaine: number; creneaux: PlageCalendrier[] }[];
}

export interface EquipementsPortail {
  collectivite: { code: string; nom: string };
  module_actif: boolean;
  debut: string | null;
  elements: EquipementPortail[];
}

export type ReservationUsager = Reservation & { collectivite: { code: string; nom: string } };

/** Les routes de l'espace usager attendent la session rouverte (rechargement de page : cookie de rafraîchissement). */
function useConnecte() {
  return useSessionUsager().etat === 'connecte';
}

export function useEquipementsPortail(code: string, debut: string) {
  const connecte = useConnecte();
  return useQuery({ queryKey: CLES_RESERVATIONS.equipements(code, debut), queryFn: ({ signal }) => requete<EquipementsPortail>('/usager/equipements', { signal, parametres: { collectivite: code, debut } }), enabled: connecte, retry: false });
}

export function useMesReservations() {
  const connecte = useConnecte();
  return useQuery({ queryKey: CLES_RESERVATIONS.mes, queryFn: ({ signal }) => requete<{ elements: ReservationUsager[] }>('/usager/reservations', { signal }).then((r) => r.elements), enabled: connecte, retry: false });
}

export const demanderReservation = (corps: Record<string, unknown>) => requete<ReservationUsager>('/usager/reservations', { methode: 'POST', corps });
export const annulerMaReservation = (id: string, motif: string) => requete<ReservationUsager>(`/usager/reservations/${id}/annuler`, { methode: 'POST', corps: { motif } });

export const STATUT_RESERVATION: Record<EtatReservation, { libelle: string; ton: TonStatut }> = {
  demandee: { libelle: 'Demande envoyée', ton: 'info' },
  a_payer: { libelle: 'Acceptée, à payer', ton: 'attention' },
  confirmee: { libelle: 'Confirmée', ton: 'ok' },
  refusee: { libelle: 'Refusée', ton: 'neutre' },
  annulee: { libelle: 'Annulée', ton: 'neutre' },
  terminee: { libelle: 'Terminée', ton: 'neutre' },
};
